import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { ApprovalFlowStatus } from '@/constants/ApprovalConstants';
import SimpleMenu from '../atoms/Menu';
import { ReasonDialog } from './ReasonDialog';

function getCancelExtraTripErrorMessage(error: unknown): string {
	if (!isAxiosError(error)) {
		return 'Erro ao cancelar ida extra. Tente novamente mais tarde.';
	}

	const status = error.response?.status;
	const apiMessage = error.response?.data?.message;

	if (status && status < 500 && typeof apiMessage === 'string' && apiMessage) {
		return apiMessage;
	}

	if (status === 403) {
		return 'Você não tem permissão para cancelar esta ida extra';
	}

	return 'Erro ao cancelar ida extra. Tente novamente mais tarde.';
}

interface ExtraTripCardMenuProps {
	onEdit: () => void;
	onCancel: () => Promise<void>;
	approvalStatus?: ApprovalFlowStatus;
	isHistoryView?: boolean;
}

const ExtraTripCardMenu = ({
	onEdit,
	onCancel,
	approvalStatus,
	isHistoryView = false,
}: ExtraTripCardMenuProps) => {
	const queryClient = useQueryClient();
	const { mutate: cancelExtraTrip } = useMutation({
		mutationFn: onCancel,
		onSuccess: () => {
			toast.success('Ida extra cancelada com sucesso', {
				theme: 'colored',
				toastId: 'success',
			});
			queryClient.invalidateQueries({
				queryKey: ['approval-pending'],
			});
			queryClient.invalidateQueries({
				queryKey: ['extra-trips-scheduled'],
			});

			setIsCancelDialogOpen(false);
		},
		onError: (error) => {
			toast.error(getCancelExtraTripErrorMessage(error), {
				theme: 'colored',
				toastId: 'error',
			});
		},
	});
	const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

	const disablingStatus: ApprovalFlowStatus[] = [
		ApprovalFlowStatus.REJECTED,
		ApprovalFlowStatus.CANCELLED,
		ApprovalFlowStatus.LACK_OF_ACTION,
	];
	const shouldDisableCancelling =
		isHistoryView ||
		(approvalStatus && disablingStatus.includes(approvalStatus)) ||
		false;
	const shouldDisableEditing =
		shouldDisableCancelling || approvalStatus === ApprovalFlowStatus.ACCEPTED;

	const options = [
		{
			name: 'Editar',
			onClick: onEdit,
			disabled: shouldDisableEditing,
		},
		{
			name: 'Cancelar',
			onClick: () => setIsCancelDialogOpen(true),
			disabled: shouldDisableCancelling,
		},
	];

	return (
		<>
			<SimpleMenu options={options} upDotsButton />
			<ReasonDialog
				title="Confirmação de cancelamento"
				description="Tem certeza que deseja cancelar esta ida extra?"
				isOpen={isCancelDialogOpen}
				setIsOpen={setIsCancelDialogOpen}
				showReasonField={false}
				onConfirmation={cancelExtraTrip}
			/>
		</>
	);
};
export default ExtraTripCardMenu;
